import type { BackendSocket, ChatSocket } from '../hooks/use-socket';
import { isSuspended, onSuspend } from './suspension';

// Guard callbacks as well as disconnecting: an emitter may already have copied
// its listener list when a preceding callback suspends the application.
export const guardSocket = <T extends BackendSocket | ChatSocket>(
    socket: T
) => {
    let disposed = false;
    const registrations = new Map<
        string,
        Map<Function, (...args: unknown[]) => void>
    >();
    const guarded = new Proxy(socket, {
        get(target, key) {
            if (key === 'on' || key === 'once') {
                return (
                    event: string,
                    callback: (...args: unknown[]) => void
                ) => {
                    if (disposed || isSuspended()) return guarded;
                    const wrapped = (...args: unknown[]) => {
                        if (!disposed && !isSuspended()) callback(...args);
                    };
                    const entries = registrations.get(event) || new Map();
                    entries.set(callback, wrapped);
                    registrations.set(event, entries);
                    target[key](event, wrapped);
                    return guarded;
                };
            }
            if (key === 'off') {
                return (event: string, callback: Function) => {
                    const wrapped = registrations.get(event)?.get(callback);
                    if (wrapped) target.off(event, wrapped);
                    registrations.get(event)?.delete(callback);
                    return guarded;
                };
            }
            const value = Reflect.get(target, key);
            if (typeof value !== 'function') return value;
            return (...args: unknown[]) => {
                if (disposed || isSuspended()) return undefined;
                return value.apply(target, args);
            };
        },
    });
    const dispose = () => {
        if (disposed) return;
        disposed = true;
        for (const [event, entries] of registrations) {
            for (const callback of entries.values())
                socket.off(event, callback);
        }
        registrations.clear();
        socket.disconnect();
    };
    const unsubscribe = onSuspend(dispose);
    return {
        socket: guarded,
        dispose: () => {
            unsubscribe();
            dispose();
        },
    };
};
